import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Send, Plus, MessageSquare, Loader2, Trash2 } from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../lib/api';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { useToast } from '../components/ui/Toast';
import { usePermissions, getPermissionTooltip } from '../hooks/usePermissions';

interface ChatSession {
  id: string;
  title: string;
  lastMessageAt?: string;
  startedAt: string;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  confidence?: number;
  createdAt: string;
}

export function ChatPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [message, setMessage] = useState('');
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { can, role } = usePermissions();
  const canSendMessages = can('chat:write');

  // Fetch sessions
  const { data: sessionsData, isLoading: sessionsLoading } = useQuery({
    queryKey: ['chatSessions'],
    queryFn: () => api.getChatSessions(),
  });

  // Fetch messages for active session
  const { data: messagesData, isLoading: messagesLoading } = useQuery({
    queryKey: ['chatMessages', activeSessionId],
    queryFn: () => api.getChatMessages(activeSessionId!),
    enabled: !!activeSessionId,
  });

  // Create session mutation
  const createSessionMutation = useMutation({
    mutationFn: () => api.createChatSession({ title: 'New Conversation' }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['chatSessions'] });
      setActiveSessionId(data.id);
    },
  });

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: ({ sessionId, content }: { sessionId: string; content: string }) =>
      api.sendChatMessage(sessionId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatMessages', activeSessionId] });
      queryClient.invalidateQueries({ queryKey: ['chatSessions'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message ?? 'Failed to send message');
    },
  });

  // Delete session mutation
  const deleteSessionMutation = useMutation({
    mutationFn: (sessionId: string) => api.deleteChatSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatSessions'] });
      if (activeSessionId) {
        setActiveSessionId(null);
      }
      toast.success('Chat session deleted');
    },
    onError: () => {
      toast.error('Failed to delete chat session');
    },
  });

  const sessions: ChatSession[] = sessionsData?.data ?? [];
  const messages: ChatMessage[] = messagesData?.data ?? [];

  // Auto-select first session or create one if none exist
  useEffect(() => {
    if (!sessionsLoading && sessions.length > 0 && !activeSessionId) {
      setActiveSessionId(sessions[0].id);
    }
  }, [sessionsLoading, sessions, activeSessionId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!message.trim()) return;

    let sessionId: string = activeSessionId ?? '';

    // Create session if none exists
    if (!sessionId) {
      const newSession = await createSessionMutation.mutateAsync();
      sessionId = newSession.id;
      setActiveSessionId(sessionId);
    }

    const content = message.trim();
    setMessage('');

    sendMessageMutation.mutate({ sessionId, content });
  };

  const handleNewChat = () => {
    createSessionMutation.mutate();
  };

  const handleDeleteSession = (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation();
    setDeleteTarget(sessionId);
  };

  return (
    <div className="flex h-[calc(100vh-8rem)]">
      {/* Sidebar */}
      <div className="w-64 border-r border-gray-200 bg-white flex flex-col">
        <div className="p-4">
          <button
            onClick={handleNewChat}
            disabled={createSessionMutation.isPending || !canSendMessages}
            title={!canSendMessages ? getPermissionTooltip(role) : undefined}
            className="w-full flex items-center justify-center px-4 py-2 bg-legacy-600 text-white rounded-md hover:bg-legacy-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createSessionMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            New Chat
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-2">
          {sessionsLoading ? (
            <div className="flex justify-center py-4">
              <LoadingSpinner size="sm" />
            </div>
          ) : sessions.length > 0 ? (
            <div className="space-y-1">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  onClick={() => setActiveSessionId(session.id)}
                  className={clsx(
                    'group flex items-center justify-between px-3 py-2 rounded-md cursor-pointer transition-colors',
                    activeSessionId === session.id
                      ? 'bg-legacy-100 text-legacy-900'
                      : 'hover:bg-gray-100 text-gray-700'
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{session.title}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(session.lastMessageAt ?? session.startedAt).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={(e) => handleDeleteSession(e, session.id)}
                    className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-opacity"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-2 py-4">
              <EmptyState
                icon={MessageSquare}
                title="No chat history"
                className="py-4"
              />
            </div>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col bg-gray-50">
        {messagesLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-legacy-100 rounded-full flex items-center justify-center mb-4">
                <MessageSquare className="h-8 w-8 text-legacy-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                How can I help you today?
              </h2>
              <p className="text-gray-600 max-w-md mb-4">
                Ask questions about your wealth strategy, get explanations for
                recommendations, or explore &ldquo;what if&rdquo; scenarios.
              </p>
              <div className="flex flex-wrap justify-center gap-2 max-w-lg">
                {['Explain my top recommendation', 'What should I prioritize?', 'How is my portfolio allocated?'].map((prompt) => (
                  <button
                    key={prompt}
                    onClick={() => { setMessage(prompt); }}
                    className="px-3 py-1.5 text-xs text-gray-600 bg-white border border-gray-200 rounded-full hover:border-legacy-300 hover:text-legacy-700 transition-colors"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={clsx(
                  'max-w-2xl rounded-lg p-4',
                  msg.role === 'user'
                    ? 'ml-auto bg-legacy-600 text-white'
                    : 'bg-white border border-gray-200'
                )}
              >
                <div className="whitespace-pre-wrap">{msg.content}</div>
                {msg.role === 'assistant' && msg.confidence && (
                  <div className="mt-2 text-xs text-gray-400">
                    {Math.round(msg.confidence * 100)}% confidence
                  </div>
                )}
              </div>
            ))}
            {sendMessageMutation.isPending && (
              <div className="max-w-2xl rounded-lg p-4 bg-white border border-gray-200">
                <div className="flex items-center space-x-2 text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Thinking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Input */}
        <div className="p-4 bg-white border-t border-gray-200">
          {/* Viewer notice */}
          {!canSendMessages && (
            <div className="max-w-3xl mx-auto mb-3 bg-yellow-50 border border-yellow-200 rounded-md p-2 text-sm text-yellow-800">
              {getPermissionTooltip(role)}
            </div>
          )}
          <div className="max-w-3xl mx-auto flex items-center space-x-2">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && canSendMessages && handleSend()}
              placeholder={canSendMessages ? "Ask about your wealth strategy..." : "View-only access"}
              className="input flex-1"
              disabled={sendMessageMutation.isPending || !canSendMessages}
            />
            <button
              onClick={handleSend}
              disabled={!message.trim() || sendMessageMutation.isPending || !canSendMessages}
              title={!canSendMessages ? getPermissionTooltip(role) : undefined}
              className={clsx(
                'p-2 rounded-md transition-colors',
                message.trim() && !sendMessageMutation.isPending && canSendMessages
                  ? 'bg-legacy-600 text-white hover:bg-legacy-700'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              )}
            >
              {sendMessageMutation.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete chat session"
        description="Are you sure you want to delete this chat session? This action cannot be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          if (deleteTarget) deleteSessionMutation.mutate(deleteTarget);
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
